import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, X, Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ProductCard from './ProductCard';
import { CatalogAPI, SiteConfigAPI, resolveImage } from '../lib/api';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useAdminEdit } from '../context/AdminEditContext';
import { useToast } from '../hooks/use-toast';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

const SortableFeatured = ({ p, onAddToCart, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <button onClick={() => onRemove(p.id)} title="Retirer des produits à la une"
        className="absolute -top-2 -right-2 z-20 bg-[#C75B27] text-white rounded-full w-7 h-7 flex items-center justify-center shadow hover:bg-[#a84d21]">
        <X className="w-4 h-4" />
      </button>
      <button {...attributes} {...listeners} title="Glisser pour déplacer"
        className="absolute -top-2 -left-2 z-20 bg-[#1F4D3F] text-white rounded-full w-7 h-7 flex items-center justify-center shadow cursor-grab active:cursor-grabbing touch-none">
        <GripVertical className="w-4 h-4" />
      </button>
      <ProductCard product={p} onAddToCart={onAddToCart} />
    </div>
  );
};

const FeaturedProducts = ({ onAddToCart }) => {
  const { t } = useTranslation();
  const { config, refresh } = useSiteConfig();
  const { editMode } = useAdminEdit();
  const { toast } = useToast();
  const ids = Array.isArray(config['home.featured_product_ids']) ? config['home.featured_product_ids'] : [];
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  useEffect(() => {
    if (ids.length === 0) { setProducts([]); return; }
    let cancelled = false;
    Promise.all(ids.map(id => CatalogAPI.product(id).catch(() => null))).then(results => {
      if (cancelled) return;
      // Garder l'ordre des ids
      const byId = Object.fromEntries(results.filter(Boolean).map(p => [p.id, p]));
      setProducts(ids.map(id => byId[id]).filter(Boolean));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ids)]);

  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    const tmr = setTimeout(() => {
      CatalogAPI.products({ q: search, limit: 8, include_empty: true }).then(setSearchResults).catch(() => {});
    }, 300);
    return () => clearTimeout(tmr);
  }, [search]);

  const saveIds = async (newIds) => {
    try {
      const full = await SiteConfigAPI.adminGet();
      full['home.featured_product_ids'] = newIds;
      await SiteConfigAPI.adminSet(full);
      await refresh();
    } catch (e) { toast({ title: 'Erreur' }); }
  };

  const removeProduct = (id) => { saveIds(ids.filter(x => x !== id)); toast({ title: 'Retiré des produits à la une' }); };
  const addProduct = (id) => { if (!ids.includes(id)) saveIds([...ids, id]); setSearch(''); setSearchResults([]); toast({ title: 'Ajouté aux produits à la une' }); };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = ids.indexOf(active.id), newI = ids.indexOf(over.id);
    if (oldI < 0 || newI < 0) return;
    saveIds(arrayMove(ids, oldI, newI));
  };

  // Hors édition : rien si vide
  if (!editMode && products.length === 0) return null;

  return (
    <section className="bg-gradient-to-br from-[#FAF7F2] via-white to-[#F5F1EA] border-y border-[#E8E0D0]">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[#E0A52E]/15 text-[#A8761E] text-xs font-semibold rounded-full mb-2">
              <Sparkles className="w-3 h-3" /> {t('home.featured_title')}
            </span>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">{t('home.featured_title')}</h2>
            <p className="text-[#7A6F5C] mt-1.5">{t('home.featured_subtitle')}</p>
          </div>
          <Link to="/categories" className="hidden md:inline-flex items-center gap-1 text-[#1F4D3F] hover:text-[#173B30] font-medium">
            {t('common.view_more')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {editMode && (
          <div className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-xl p-3 mb-4">
            <div className="text-xs text-[#7A6F5C] mb-2">Glissez les cartes pour réordonner, la croix pour retirer, ou ajoutez un produit ci-dessous.</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8F82]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ajouter un produit à la une…"
                className="w-full pl-9 pr-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none" />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border border-[#E8E0D0] rounded-lg divide-y divide-[#F5F1EA] max-h-56 overflow-y-auto bg-white">
                {searchResults.map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2">
                    <img src={resolveImage(r.image)} alt="" className="w-9 h-9 rounded object-cover bg-[#FAF7F2]" />
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{r.name}</div></div>
                    {ids.includes(r.id) ? <span className="text-xs text-emerald-700">Déjà présent</span>
                      : <button onClick={() => addProduct(r.id)} className="bg-[#1F4D3F] text-white text-xs px-3 py-1.5 rounded flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {editMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((p) => (
                  <SortableFeatured key={p.id} p={p} onAddToCart={onAddToCart} onRemove={removeProduct} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
            ))}
          </div>
        )}
        {editMode && products.length === 0 && <div className="text-center text-[#7A6F5C] py-8">Aucun produit à la une. Ajoutez-en via la recherche ci-dessus.</div>}
      </div>
    </section>
  );
};

export default FeaturedProducts;
